import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot, GripVertical, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const TOKENS = ["{first_name}", "{name}", "{country}", "{counsellor}"];

export default function WaAutomationPanel() {
  const [cfg, setCfg] = useState(null);
  const [fields, setFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/admin/wa-automation")
      .then((r) => {
        setCfg(r.data.automation);
        setFields(r.data.fields || {});
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const setQ = (i, patch) =>
    setCfg((c) => ({ ...c, questions: c.questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)) }));
  const addQ = () =>
    setCfg((c) => ({ ...c, questions: [...(c.questions || []), { prompt: "", field: "note" }] }));
  const removeQ = (i) => setCfg((c) => ({ ...c, questions: c.questions.filter((_, idx) => idx !== i) }));
  const move = (i, dir) =>
    setCfg((c) => {
      const arr = [...c.questions];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return c;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...c, questions: arr };
    });

  const save = async () => {
    if (cfg.enabled && (cfg.questions || []).some((q) => !q.prompt.trim())) {
      toast.error("Every question needs a prompt");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/wa-automation", {
        enabled: cfg.enabled,
        welcome: cfg.welcome || "",
        completion: cfg.completion || "",
        questions: (cfg.questions || []).map((q) => ({ prompt: q.prompt, field: q.field })),
      });
      setCfg(data.automation);
      toast.success("Automation saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) {
    return <div className="py-6 text-center text-sm text-slate-400"><Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="space-y-4 rounded-xl border border-indigo-100 bg-indigo-50/40 p-4" data-testid="wa-automation-panel">
      <div className="flex items-center gap-2">
        <Bot className="h-5 w-5 text-indigo-500" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-[#0A2540]">Lead-warming automation</div>
          <div className="text-[11px] text-slate-500">When a lead messages on WhatsApp, the bot asks your questions, captures the answers, and warms the lead.</div>
        </div>
        <Switch checked={cfg.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid="wa-auto-enabled" />
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[11px] text-slate-500">Tokens:</span>
        {TOKENS.map((t) => (
          <span key={t} className="rounded bg-white px-1.5 py-0.5 font-mono text-[10px] text-indigo-600">{t}</span>
        ))}
      </div>

      <div>
        <Label className="text-xs text-slate-500">Welcome message (sent with the first question)</Label>
        <Textarea rows={2} value={cfg.welcome || ""} onChange={(e) => set({ welcome: e.target.value })} placeholder="Hi {first_name}! Thanks for reaching out to MedAdmits." className="mt-1 resize-none" data-testid="wa-auto-welcome" />
      </div>

      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <Label className="text-xs text-slate-500">Questions (asked one at a time, on each reply)</Label>
          <Button size="sm" variant="ghost" onClick={addQ} className="h-7 text-indigo-600 hover:bg-indigo-100" data-testid="wa-auto-add-q">
            <Plus className="mr-1 h-3.5 w-3.5" /> Add
          </Button>
        </div>
        <div className="space-y-2">
          {(cfg.questions || []).length === 0 && (
            <div className="rounded-lg border border-dashed border-indigo-200 py-3 text-center text-[11px] text-slate-400">
              No questions yet. The bot will just send the welcome message.
            </div>
          )}
          {(cfg.questions || []).map((q, i) => (
            <div key={i} className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white p-2" data-testid={`wa-auto-q-${i}`}>
              <div className="flex flex-col pt-1.5 text-slate-300">
                <button onClick={() => move(i, -1)} className="hover:text-slate-500" aria-label="Move up">▲</button>
                <button onClick={() => move(i, 1)} className="hover:text-slate-500" aria-label="Move down">▼</button>
              </div>
              <div className="flex-1 space-y-1.5">
                <Input value={q.prompt} onChange={(e) => setQ(i, { prompt: e.target.value })} placeholder="e.g. What's your NEET score?" className="h-8 text-xs" data-testid={`wa-auto-q-prompt-${i}`} />
                <Select value={q.field} onValueChange={(v) => setQ(i, { field: v })}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`wa-auto-q-field-${i}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(fields).map(([k, label]) => (
                      <SelectItem key={k} value={k}>Save to: {label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <button onClick={() => removeQ(i)} className="rounded-md p-1.5 text-rose-400 hover:bg-rose-50" data-testid={`wa-auto-q-del-${i}`} aria-label="Remove">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs text-slate-500">Completion message (after the last question)</Label>
        <Textarea rows={2} value={cfg.completion || ""} onChange={(e) => set({ completion: e.target.value })} placeholder="Thanks {first_name}! A counsellor will reach out shortly." className="mt-1 resize-none" data-testid="wa-auto-completion" />
      </div>

      <div className="flex items-center justify-between">
        <span className="text-[10px] text-slate-400">Replies are also marked Warm automatically. Lead can opt out by replying STOP.</span>
        <Button size="sm" onClick={save} disabled={saving} className="bg-[#0A2540] hover:bg-[#1E3A5F]" data-testid="wa-auto-save">
          {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Save automation
        </Button>
      </div>
    </div>
  );
}
